import { Row, Col, Card } from "react-bootstrap";
import { Photo } from "./content";
import { useImageSrc, useSite } from "./content/context";
import { AddTile, ItemControls } from "./edit/primitives";
import { compact, moveItem, removeItem, replaceItem } from "./edit/list";

interface Props {
  /** Defaults to the main gallery. Photo gallery sections pass their own photos. */
  photos?: Photo[];
  onChange?: (photos: Photo[]) => void;
}

const Gallery = ({ photos: ownPhotos, onChange }: Props) => {
  const { data, edit } = useSite();
  const imageSrc = useImageSrc();
  const photos = ownPhotos ?? data.gallery;
  const save = (next: Photo[]) => (onChange ? onChange(next) : edit?.update("gallery", next));

  const describe = async (index: number) => {
    if (!edit) return;
    const result = await edit.openForm({
      title: "Edit photo",
      fields: [
        { name: "image", label: "Photo", type: "image", required: true },
        { name: "alt", label: "Description", hint: "Describes the photo for people using screen readers." },
      ],
      values: { ...photos[index] },
    });
    if (result && "values" in result) save(replaceItem(photos, index, compact<Photo>(result.values)));
  };

  const addPhotos = async () => {
    if (!edit) return;
    const paths = await edit.chooseImages();
    if (paths.length) save([...photos, ...paths.map((image) => ({ image, alt: "Arc Flash photo" }))]);
  };

  return (
    <Row className="mb-3">
      {photos.map(({ image, alt }, index) => {
        const src = imageSrc(image);
        return src ? (
          <Col md={4} className="mb-4" key={`${image}-${index}`}>
            <Card className={edit ? "af-frame" : undefined}>
              <Card.Img variant="top" className="gallery-photo" src={src} alt={alt || "Arc Flash photo"} />
              <ItemControls
                noun="photo"
                index={index}
                count={photos.length}
                onChangePhoto={(path) => save(replaceItem(photos, index, { ...photos[index], image: path }))}
                onEdit={() => describe(index)}
                onMove={(from, to) => save(moveItem(photos, from, to))}
                onRemove={() => save(removeItem(photos, index))}
              />
            </Card>
          </Col>
        ) : null;
      })}
      {edit ? (
        <Col md={4} className="mb-4">
          <AddTile label="Add photos" onClick={addPhotos} />
        </Col>
      ) : null}
    </Row>
  );
};

export default Gallery;
